// frontend/src/pages/guides/GuideByline.js
// PRD-193 WP3 — the visible freshness + authorship signal on every guide.
//
// Before this, 0 of 19 guides rendered a visible "Last updated" line. Each one DID already
// emit `"author": {"@type":"Organization","name":"DriftAlarm"}` in its Article schema, so the
// gap was never the schema field -- it was that a human (and an answer engine reading the
// rendered page) had no on-page signal of when the guide was last touched or who maintains it.
//
// The date is read from guidesCatalog.js, the same field WP3's sitemap generator uses for
// <lastmod> and the same one each component's Article schema carries. One edit to the
// catalog's dateModified therefore moves the visible line, the sitemap, and the schema
// together -- they cannot drift apart.
//
// The authorship line resolves PRD-193 Open Question 2 (Ward, 2026-08-13): anonymous brand
// voice, no founder name, linking to /how-it-works as the methodology target. When the
// reserved PRD-185 /trust page ships, repoint METHODOLOGY_PATH there -- do NOT create /trust
// in PRD-193.

import React from 'react';
import { Link } from 'react-router-dom';
import { getGuideByPath } from '../../config/guidesCatalog';

const METHODOLOGY_PATH = '/how-it-works';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Format 'YYYY-MM-DD' as 'March 6, 2026'.
 *
 * Deliberately string-sliced rather than `new Date(...).toLocaleDateString()`: this component
 * renders during prerender in Node AND in the browser, and a Date-based format would drift
 * with the host timezone/locale, producing a hydration mismatch and an unstable prerendered
 * date. Returns null on anything unparseable, so a bad catalog value degrades to no byline
 * rather than "Invalid Date".
 */
export function formatByline(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  const month = MONTHS[parseInt(m[2], 10) - 1];
  if (!month) return null;
  return `${month} ${parseInt(m[3], 10)}, ${m[1]}`;
}

/**
 * @param {string}  [path]          route path, e.g. '/guides/exposed-rdp' -- resolves the date
 *                                  from the catalog. Preferred.
 * @param {string}  [dateModified]  explicit ISO date, overriding the catalog lookup. Used by
 *                                  the index, which has no single guide of its own.
 * @param {string}  [label]         leading label; defaults to 'Last updated'.
 */
function GuideByline({ path, dateModified, label = 'Last updated' }) {
  const guide = path ? getGuideByPath(path) : null;
  const raw = dateModified || (guide && guide.dateModified);
  const ymd = /^\d{4}-\d{2}-\d{2}/.test(String(raw || '')) ? String(raw).slice(0, 10) : null;
  const pretty = formatByline(ymd);
  if (!pretty) return null;

  return (
    <div className="guide-byline">
      <span className="guide-byline-date">
        {label} <time dateTime={ymd}>{pretty}</time>
      </span>
      <span className="guide-byline-sep" aria-hidden="true">·</span>
      <span className="guide-byline-author">
        Written and maintained by the{' '}
        <Link to={METHODOLOGY_PATH}>DriftAlarm research team</Link>
      </span>
    </div>
  );
}

export default GuideByline;
